import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { useIsAdmin } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";

export type ModuleStatus = "active" | "inactive" | "trial" | "coming_soon";

export interface ResolvedModule {
  module_key: string;
  name: string;
  description: string;
  icon: string;
  is_core: boolean;
  sort_order: number;
  module_group: string;
  module_tier: string;
  audience: string;
  effective_status: ModuleStatus;
  source: string;
  restricted_by: "platform" | "association" | "org" | null;
  platform_status: ModuleStatus;
  association_status: ModuleStatus;
  org_status: ModuleStatus;
}

export interface ResolvedCapability {
  capability_key: string;
  module_key: string;
  name: string;
  description: string;
  effective_status: ModuleStatus;
  restricted_by: "platform" | "association" | "org" | null;
}

interface ResolvedModulesData {
  modules: ResolvedModule[];
  capabilities: ResolvedCapability[];
}

export const useModules = () => {
  const { user } = useAuth();
  const { orgId } = useOrg();
  const { isMasterAdmin } = useIsAdmin();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["resolved_modules", orgId || "platform"],
    queryFn: async (): Promise<ResolvedModulesData> => {
      const { data: result, error } = await supabase.rpc("get_resolved_modules", { _org_id: orgId || null });

      if (error) throw error;
      const r = result as unknown as { modules?: ResolvedModule[]; capabilities?: ResolvedCapability[] } | null;
      return {
        modules: r?.modules ?? [],
        capabilities: r?.capabilities ?? [],
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const modules = data?.modules ?? [];
  const capabilities = data?.capabilities ?? [];

  const hasModule = (moduleKey: string): boolean => {
    if (isMasterAdmin) return true;
    const mod = modules.find((m) => m.module_key === moduleKey);
    return mod?.effective_status === "active";
  };

  const hasCapability = (capabilityKey: string): boolean => {
    if (isMasterAdmin) return true;
    const cap = capabilities.find((c) => c.capability_key === capabilityKey);
    return cap?.effective_status === "active";
  };

  const getModule = (moduleKey: string): ResolvedModule | undefined => {
    return modules.find((m) => m.module_key === moduleKey);
  };

  const getCapability = (capabilityKey: string): ResolvedCapability | undefined => {
    return capabilities.find((c) => c.capability_key === capabilityKey);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["resolved_modules"] });
  };

  return {
    modules,
    capabilities,
    hasModule,
    hasCapability,
    getModule,
    getCapability,
    isLoading,
    invalidate,
  };
};

/**
 * Mutations for toggling modules at different levels
 */
export const useToggleModule = () => {
  const queryClient = useQueryClient();

  const togglePlatform = useMutation({
    mutationFn: async ({ moduleKey, status }: { moduleKey: string; status: ModuleStatus }) => {
      const { error } = await supabase.rpc("toggle_platform_module", {
        _module_key: moduleKey,
        _status: status,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["resolved_modules"] }),
  });

  const toggleAssociation = useMutation({
    mutationFn: async ({
      associationId,
      moduleKey,
      status,
    }: {
      associationId: string;
      moduleKey: string;
      status: ModuleStatus;
    }) => {
      const { error } = await supabase.rpc("toggle_association_module", {
        _association_id: associationId,
        _module_key: moduleKey,
        _status: status,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["resolved_modules"] }),
  });

  const toggleOrg = useMutation({
    mutationFn: async ({ orgId, moduleKey, status }: { orgId: string; moduleKey: string; status: ModuleStatus }) => {
      const { error } = await supabase.rpc("toggle_org_module", {
        _org_id: orgId,
        _module_key: moduleKey,
        _status: status,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["resolved_modules"] }),
  });

  return { togglePlatform, toggleAssociation, toggleOrg };
};
